// src/pages/RidePanel.js
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import RideBottomNav from "../components/RideBottomNav";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_BASE = API_WRK?.replace("/api/worker", "");

// 🔥 GOOGLE AUTOCOMPLETE
const useGoogleAutocomplete = (ref, onSelect) => {
  useEffect(() => {
    if (!ref.current || !window.google) return;

    const autocomplete = new window.google.maps.places.Autocomplete(ref.current, {
      fields: ["formatted_address", "name"],
      types: ["(cities)"],
    });

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const value = place.formatted_address || place.name || "";
      ref.current.value = value;
      onSelect(value);
    });
  }, [ref, onSelect]);
};

const RidePanel = () => {
  const navigate = useNavigate();
  const workerId = localStorage.getItem("workerId");

  // INPUT REFS
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const stopCityRef = useRef(null);
  const stopPickupRef = useRef(null);

  // FORM DATA
  const [formData, setFormData] = useState({
    from: "",
    to: "",
    date: "",
    time: "",
    seatsAvailable: "",
    pricePerSeat: "",
    pickupLocation: "",
    stops: [],
    currentStop: { name: "", pickup: "", price: "", time: "" },
    bookingType: "manual",
    dropOffNotes: "",
    rideOptions: {
      luggage: "none",
      winterTires: false,
      twoPerRow: false,
      petsAllowed: false,
      bikesAllowed: false,
      skisAllowed: false,
    },
  });

  const [confirmChecked, setConfirmChecked] = useState(false);

  // 🔥 APPLY AUTOCOMPLETE
  useGoogleAutocomplete(fromRef, (value) =>
    setFormData((prev) => ({ ...prev, from: value, pickupLocation: value }))
  );

  useGoogleAutocomplete(toRef, (value) =>
    setFormData((prev) => ({ ...prev, to: value }))
  );

  useGoogleAutocomplete(stopCityRef, (value) =>
    setFormData((prev) => ({
      ...prev,
      currentStop: { ...prev.currentStop, name: value },
    }))
  );

  useGoogleAutocomplete(stopPickupRef, (value) =>
    setFormData((prev) => ({
      ...prev,
      currentStop: { ...prev.currentStop, pickup: value },
    }))
  );

  // INPUT HANDLERS
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStopChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      currentStop: { ...prev.currentStop, [name]: value },
    }));
  };

  const addStop = () => {
    const { name, pickup, price, time } = formData.currentStop;
    if (!name || !pickup || !price || !time)
      return alert("Fill all stop fields");

    setFormData((prev) => ({
      ...prev,
      stops: [...prev.stops, { name, pickup, price, time }],
      currentStop: { name: "", pickup: "", price: "", time: "" },
    }));

    if (stopCityRef.current) stopCityRef.current.value = "";
    if (stopPickupRef.current) stopPickupRef.current.value = "";
  };

  // SUBMIT RIDE
  const handleSubmit = async () => {
    try {
      const res = await axios.post(`${API_BASE}/api/ride/post`, {
        workerId,
        ...formData,
        pricePerSeat: parseFloat(formData.pricePerSeat),
        seatsAvailable: parseInt(formData.seatsAvailable),
      });

      navigate("/ride-success", {
        state: {
          ...formData,
          rideId: res.data?.ride?._id,
        },
      });
    } catch (err) {
      console.error("❌ Ride post failed:", err);
      alert("❌ Failed to post ride");
    }
  };

  // UI
  return (
    <div className="min-h-screen bg-gray-50 pb-28 px-4">
      <div className="max-w-2xl mx-auto mt-6 space-y-10">
        {/* HEADER */}
        <h1 className="text-3xl font-bold text-center text-blue-800">
          BYN Rides
        </h1>
        <p className="text-center text-gray-600 -mt-2">
          Post a trip & earn more per seat.
        </p>

        {/* FROM / TO */}
        <div className="bg-white p-6 rounded-2xl shadow space-y-4">
          <h2 className="text-xl font-bold">Where are you going?</h2>

          <input
            ref={fromRef}
            placeholder="Pickup City"
            className="w-full border px-4 py-3 rounded-lg"
          />

          <input
            ref={toRef}
            placeholder="Drop-off City"
            className="w-full border px-4 py-3 rounded-lg"
          />
        </div>

        {/* DATE & TIME */}
        <div className="bg-white p-6 rounded-2xl shadow space-y-4">
          <h2 className="text-xl font-bold">Ride Schedule</h2>

          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="w-full border px-4 py-3 rounded-lg"
          />

          <input
            type="time"
            name="time"
            value={formData.time}
            onChange={handleChange}
            className="w-full border px-4 py-3 rounded-lg"
          />
        </div>

        {/* STOPS */}
        <div className="bg-white p-6 rounded-2xl shadow space-y-4">
          <h2 className="text-xl font-bold">Add Optional Stops</h2>

          <input
            ref={stopCityRef}
            placeholder="Stop city"
            className="w-full border px-4 py-3 rounded-lg"
          />

          <input
            ref={stopPickupRef}
            placeholder="Pickup location"
            className="w-full border px-4 py-3 rounded-lg"
          />

          <input
            name="price"
            value={formData.currentStop.price}
            onChange={handleStopChange}
            placeholder="Stop price ($)"
            className="w-full border px-4 py-3 rounded-lg"
          />

          <input
            name="time"
            type="time"
            value={formData.currentStop.time}
            onChange={handleStopChange}
            className="w-full border px-4 py-3 rounded-lg"
          />

          <button
            onClick={addStop}
            className="bg-blue-600 text-white py-2 rounded-lg w-full"
          >
            + Add Stop
          </button>

          {formData.stops.length > 0 && (
            <ul className="pt-2 text-sm text-gray-700">
              {formData.stops.map((s, i) => (
                <li key={i}>
                  {s.name} → {s.pickup} @ {s.time} (${s.price})
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* SEATS & PRICE PER SEAT ✅ (the missing section) */}
        <div className="bg-white p-6 rounded-2xl shadow space-y-4">
          <h2 className="text-xl font-bold">Seats & Price Per Seat</h2>

          <input
            type="number"
            name="seatsAvailable"
            value={formData.seatsAvailable}
            onChange={handleChange}
            placeholder="Available seats"
            className="w-full border px-4 py-3 rounded-lg"
          />

          <input
            type="number"
            name="pricePerSeat"
            value={formData.pricePerSeat}
            onChange={handleChange}
            placeholder="Price per seat"
            className="w-full border px-4 py-3 rounded-lg"
          />
        </div>

        {/* RIDE OPTIONS */}
        <div className="bg-white p-6 rounded-2xl shadow space-y-6">
          <h2 className="text-xl font-bold text-gray-800">Ride Options</h2>

          {[
            {
              title: "🧳 Luggage",
              key: "luggage",
              options: [
                { label: "None", value: "none" },
                { label: "Small", value: "small" },
                { label: "Large", value: "large" },
              ],
            },
            {
              title: "Winter Tires",
              key: "winterTires",
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
            },
            {
              title: "2 Per Row Max",
              key: "twoPerRow",
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
            },
            {
              title: "Pets Allowed",
              key: "petsAllowed",
              options: [
                { label: "Allowed", value: true },
                { label: "No Pets", value: false },
              ],
            },
            {
              title: "Bikes Allowed",
              key: "bikesAllowed",
              options: [
                { label: "Allowed", value: true },
                { label: "No Bikes", value: false },
              ],
            },
            {
              title: "Skis Allowed",
              key: "skisAllowed",
              options: [
                { label: "Allowed", value: true },
                { label: "No Skis", value: false },
              ],
            },
          ].map((item) => (
            <div key={item.key} className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">
                {item.title}
              </label>

              <div className="flex gap-2 flex-wrap">
                {item.options.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        rideOptions: {
                          ...prev.rideOptions,
                          [item.key]: opt.value,
                        },
                      }))
                    }
                    className={`px-4 py-1 rounded-full text-sm border font-medium transition ${
                      formData.rideOptions[item.key] === opt.value
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* DROP-OFF NOTES */}
        <div className="bg-white p-6 rounded-2xl shadow space-y-4">
          <h2 className="text-xl font-bold text-gray-800">
            Other Drop-Off Notes
          </h2>

          <textarea
            name="dropOffNotes"
            value={formData.dropOffNotes}
            onChange={handleChange}
            placeholder="e.g. Can drop at Kelowna, Surrey, Hope if needed."
            className="w-full border px-4 py-3 rounded-lg min-h-[80px]"
          />
        </div>

        {/* BOOKING TYPE */}
        <div className="bg-white p-6 rounded-2xl shadow space-y-4">
          <h2 className="text-xl font-bold">Booking Type</h2>

          <select
            name="bookingType"
            value={formData.bookingType}
            onChange={handleChange}
            className="w-full border px-4 py-3 rounded-lg"
          >
            <option value="manual">Manual Review</option>
            <option value="instant">Instant Booking</option>
          </select>
        </div>

        {/* CONFIRM */}
        <div className="bg-white p-6 rounded-2xl shadow space-y-4">
          <label className="flex gap-2 text-sm">
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
            />
            I confirm all ride details are accurate and ready to post.
          </label>

          <button
            disabled={!confirmChecked}
            onClick={handleSubmit}
            className={`w-full py-3 rounded-lg text-white font-semibold ${
              confirmChecked
                ? "bg-green-600 hover:bg-green-700"
                : "bg-gray-400"
            }`}
          >
            Post Ride
          </button>
        </div>
      </div>

      <RideBottomNav />
    </div>
  );
};

export default RidePanel;
